import { ActionIcon, Button, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import { useGetApi, useTheme } from "src/hooks";
import { Flex } from "src/styled";
import { getYachtId } from "src/utils";

import ReportCrewSelectorModal from "./ReportCrewSelectorModal/ReportCrewSelectorModal";

interface Props {
  control: any;
  watch: (name: string) => any;
  setValue: (name: string, value: any) => void;
  multiple?: boolean;
  reportType?: string;
  error?: boolean;
}
const ReportCrewSelector = ({
  multiple = true,
  watch,
  setValue,
  reportType,
  error,
}: Props) => {
  const theme = useTheme();
  const [opened, { open, close }] = useDisclosure();
  const selectedUsers: string[] = watch("data.selectedUsers") ?? [];
  const status: string = watch("data.crewStatus");
  const typeRef = useRef(reportType);

  useEffect(() => {
    if (!status) setValue("data.crewStatus", "*");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set default crew status once
  }, [status]);

  useEffect(() => {
    if (typeRef.current !== reportType) {
      setValue("data.selectedUsers", []);
      typeRef.current = reportType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear selection when report type changes
  }, [reportType]);

  const { data } = useGetApi<{ list: any[] }>({
    request: "get-users-full",
    body: {
      params: {
        yachtid: getYachtId(),
      },
    },
  });

  const handleDeleteUser = (userid: string) => {
    const newUsers = selectedUsers.filter((u: string) => u !== userid);
    setValue("data.selectedUsers", newUsers);
  };

  const getUserName = (userid: string) => {
    const user = data?.list.find((u: any) => u.userid === userid);
    return `${user?.ref} - ${user?.full_name}`;
  };

  return (
    <Flex direction={multiple ? "column" : "row"} mt={20}>
      <Button
        onClick={open}
        w={150}
        variant="outline"
        style={{
          borderColor: error ? theme.colors.redText[0] : undefined,
          borderWidth: error ? 2 : undefined,
          color: error ? theme.colors.redText[0] : undefined,
        }}>
        Select Crew
      </Button>
      {selectedUsers?.length > 0 && multiple && (
        <Text mt={multiple ? 5 : 0} size="sm">
          Selected Crew:
        </Text>
      )}
      <Flex gap={5} wrap="wrap" style={{ maxHeight: 200, overflowY: "scroll" }}>
        {selectedUsers?.map(
          (userid: string) =>
            getUserName(userid) && (
              <Flex
                key={userid}
                align="center"
                gap={2}
                style={{
                  marginLeft: multiple ? 0 : 10,
                  border: `2px solid ${theme.colors.main[4]}`,
                  padding: "3px 10px",
                  borderRadius: 10,
                }}>
                <Text size="sm">{getUserName(userid)}</Text>
                {multiple && (
                  <ActionIcon
                    color="red"
                    onClick={() => handleDeleteUser(userid)}>
                    <IconTrash size={18} />
                  </ActionIcon>
                )}
              </Flex>
            ),
        )}
      </Flex>
      <ReportCrewSelectorModal
        multiple={multiple}
        allUsers={data?.list ?? []}
        opened={opened}
        close={close}
        watch={watch}
        setValue={setValue}
        reportType={reportType}
      />
    </Flex>
  );
};
export default ReportCrewSelector;
